import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearCachedSession } from "@/core/hooks/useOfflineSession";
import { Database, Trash2, RefreshCw, Cloud, CloudOff, AlertTriangle, LogOut, BookOpen, ChevronRight } from "lucide-react";
import { PageLayout } from "@/components/templates/page-layout";
import { Button } from "@/components/ui/button";
import { seedDemoData, clearAllData } from "@/core/offline/seed";
import { useSyncEngine } from "@/core/hooks/useSyncEngine";

export default function ReglagesPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const { syncState, pendingCount, stuckCount, lastSyncAt, isStale, isOnline, forceSync } = useSyncEngine();
  const [syncLoading, setSyncLoading] = useState(false);

  function handleLogout() {
    clearCachedSession();
    localStorage.removeItem("ultra-mesure-token");
    navigate("/login", { replace: true });
  }

  async function handleForceSync() {
    setSyncLoading(true);
    try {
      const result = await forceSync();
      if (result?.success) {
        const sent = Object.values(result.pushed).reduce((a: number, b: number) => a + b, 0);
        const extra: string[] = [];
        if (result.preservedUnsynced) extra.push(`${result.preservedUnsynced} préservée(s) (données locales non synchronisées)`);
        if (result.stuckCount) extra.push(`${result.stuckCount} bloquée(s)`);
        setStatus(
          `Synchronisation réussie ! ${sent} éléments envoyés.` +
            (extra.length ? ` ${extra.join(", ")}.` : "")
        );
      } else if (result) {
        setStatus(`Sync partielle : ${result.errors.join(", ")}`);
      }
    } catch {
      setStatus("Erreur lors de la synchronisation.");
    } finally {
      setSyncLoading(false);
    }
  }

  async function handleSeed() {
    setLoading(true);
    setStatus(null);
    try {
      const result = await seedDemoData();
      setStatus(`Données chargées : ${result.sites} sites, ${result.zones} zones.`);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }

  async function handleClear() {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    setConfirmClear(false);
    setLoading(true);
    setStatus(null);
    try {
      await clearAllData();
      setStatus("Données locales supprimées. Elles seront re-téléchargées à la prochaine sync.");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageLayout title="Réglages">
      <div className="flex flex-col gap-6">
        <p className="text-sm text-muted-foreground">
          Paramètres de l&apos;application
        </p>

        {/* Section synchronisation */}
        <div className="rounded-lg border border-border p-4">
          <h3 className="mb-3 text-sm font-semibold">Synchronisation</h3>

          {/* Statut */}
          <div className="mb-3 flex items-center gap-2 text-sm">
            {isOnline ? (
              <Cloud className="size-4 text-emerald-500" />
            ) : (
              <CloudOff className="size-4 text-muted-foreground" />
            )}
            <span className={isOnline ? "text-emerald-600" : "text-muted-foreground"}>
              {isOnline ? "En ligne" : "Hors ligne"}
            </span>
          </div>

          {/* Compteur pending */}
          {pendingCount > 0 && (
            <p className="mb-2 text-sm text-muted-foreground">
              {pendingCount} modification(s) en attente de synchronisation
            </p>
          )}

          {/* Alerte entités bloquées (C5 : échecs répétés > cap retries) */}
          {stuckCount > 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-amber-50 p-2 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
              <AlertTriangle className="size-4 shrink-0" />
              <span>
                {stuckCount} modification(s) bloquée(s) après échecs répétés —
                relancez la synchronisation pour réessayer
              </span>
            </div>
          )}

          {/* Alerte stale */}
          {isStale && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-amber-50 p-2 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
              <AlertTriangle className="size-4 shrink-0" />
              <span>Données non synchronisées depuis plus de 48h</span>
            </div>
          )}

          {/* Dernière sync */}
          {lastSyncAt && (
            <p className="mb-3 text-xs text-muted-foreground">
              Dernière sync : {lastSyncAt.toLocaleString("fr-FR")}
            </p>
          )}

          {/* Bouton forcer sync */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleForceSync}
            disabled={syncLoading || syncState === "syncing" || !isOnline}
            loading={syncLoading || syncState === "syncing"}
          >
            <RefreshCw className="size-4" />
            Forcer la synchronisation
          </Button>
        </div>

        {/* Section donnees de test */}
        <div className="rounded-lg border border-border p-4">
          <h3 className="mb-3 text-sm font-semibold">Données de test</h3>
          <div className="flex flex-col gap-2">
            {/* M8 : données de démo réservées au dev (sinon poussées en prod) */}
            {import.meta.env.DEV && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSeed}
                disabled={loading}
              >
                <Database className="size-4" />
                Charger les données de démo
              </Button>
            )}
            {confirmClear ? (
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleClear}
                  disabled={loading}
                >
                  <Trash2 className="size-4" />
                  Confirmer la suppression
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmClear(false)}
                >
                  Annuler
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClear}
                disabled={loading}
                className="text-destructive"
              >
                <Trash2 className="size-4" />
                Vider toutes les données
              </Button>
            )}
          </div>
          {status && (
            <p className="mt-3 text-sm text-muted-foreground">{status}</p>
          )}
        </div>
        {/* Section librairie */}
        <div className="rounded-lg border border-border p-4">
          <h3 className="mb-3 text-sm font-semibold">Librairie de contenus</h3>
          <p className="mb-3 text-xs text-muted-foreground">
            Gérez vos blocs de texte réutilisables pour les rapports.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/library")}
          >
            <BookOpen className="size-4" />
            Gérer ma librairie
            <ChevronRight className="size-4 ml-auto" />
          </Button>
        </div>

        {/* Section compte */}
        <div className="rounded-lg border border-border p-4">
          <h3 className="mb-3 text-sm font-semibold">Compte</h3>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive"
            onClick={handleLogout}
          >
            <LogOut className="size-4" />
            Se déconnecter
          </Button>
        </div>
      </div>
    </PageLayout>
  );
}
